import { ChevronDown, ChevronUp, List } from "lucide-react";
import { FOCUS_RING, OverflowMode, VISIBLE_OPTIONS, cx } from "./tokens";

export interface FilterOverflowProps {
  mode: OverflowMode;
  total: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onViewAll: () => void;
  /** Names the collection for screen readers, e.g. "matérias". */
  noun: string;
}

const linkClass = cx(
  "inline-flex items-center gap-1.5 rounded-chip px-2 py-1",
  "text-[13px] font-medium text-brand transition-colors hover:bg-surface-hover",
  FOCUS_RING,
);

/**
 * The one escalation path for a large option set (§23.7).
 *
 * Small overflow expands in place; a genuinely large set opens the full list
 * instead. Exactly one control is ever visible — no arrow paging, no "ver mais"
 * sitting next to a "ver todas".
 */
export default function FilterOverflow({
  mode,
  total,
  expanded,
  onToggleExpand,
  onViewAll,
  noun,
}: FilterOverflowProps) {
  if (mode === "none") return null;

  if (mode === "inline") {
    const hidden = total - VISIBLE_OPTIONS;
    return (
      <button
        type="button"
        onClick={onToggleExpand}
        className={linkClass}
        aria-expanded={expanded}
        aria-label={
          expanded ? `Mostrar menos ${noun}` : `Mostrar mais ${hidden} ${noun}`
        }
      >
        {expanded ? (
          <>
            <ChevronUp className="h-4 w-4" aria-hidden="true" />
            Mostrar menos
          </>
        ) : (
          <>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
            {`+ ${hidden} mais`}
          </>
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onViewAll}
      className={linkClass}
      aria-label={`Ver todas as ${total} opções de ${noun}`}
    >
      <List className="h-4 w-4" aria-hidden="true" />
      {`Ver todas (${total})`}
    </button>
  );
}
